import React, { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useMutation, useApolloClient, useLazyQuery } from "@apollo/client";
import {
  ADD_APPEARANCE,
  GET_APPEARANCES_BY_MATCH_ID,
  GET_APPEARANCES_BY_SEASON_ID,
  APPEARANCES_WITH_PERMISSION,
} from "../../GraphQL/Appearances.js";
import useNotification from "../../hooks/useNotification.js";

const QuickEntryTable = ({ players, matchId, teamId, seasonId, onDone }) => {
  const { notifySuccess, notifyError } = useNotification();
  const client = useApolloClient();
  const [addAppearance] = useMutation(ADD_APPEARANCE);
  const [fetchSeasonAppearances] = useLazyQuery(GET_APPEARANCES_BY_SEASON_ID);

  const [stats, setStats] = useState(() =>
    Object.fromEntries(players.map((p) => [p.id, { goals: 0, assists: 0 }]))
  );
  const [saving, setSaving] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleStatChange = (playerId, field, value) => {
    const parsed = parseInt(value, 10);
    setStats((prev) => ({
      ...prev,
      [playerId]: { ...prev[playerId], [field]: isNaN(parsed) ? 0 : parsed },
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        players.map((p) =>
          addAppearance({
            variables: {
              matchId: parseInt(matchId),
              teamId: parseInt(teamId),
              playerId: parseInt(p.id),
              goals: stats[p.id]?.goals ?? 0,
              assists: stats[p.id]?.assists ?? 0,
              passes: 0,
              interceptions: 0,
              tackles: 0,
              yellowCards: 0,
              redCards: 0,
            },
          })
        )
      );
      await client.refetchQueries({
        include: [GET_APPEARANCES_BY_MATCH_ID, APPEARANCES_WITH_PERMISSION],
      });
      notifySuccess("Appearances saved.");
      onDone();
    } catch (err) {
      setError(err.message || "Failed to save appearances.");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyFromLastMatch = async () => {
    setCopyLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await fetchSeasonAppearances({
        variables: { id: parseInt(seasonId) },
        fetchPolicy: "network-only",
      });

      if (queryError) throw new Error(queryError.message);

      const allAppearances = data?.appearancesBySeasonId ?? [];

      // Filter to this team, exclude current match
      const teamAppearances = allAppearances.filter(
        (a) =>
          String(a.team.id) === String(teamId) &&
          String(a.match.id) !== String(matchId)
      );

      if (teamAppearances.length === 0) {
        notifyError("No previous match found for this team.");
        return;
      }

      // Group by match ID, take the highest match ID as most recent
      const matchGroups = {};
      teamAppearances.forEach((a) => {
        const mid = a.match.id;
        if (!matchGroups[mid]) matchGroups[mid] = [];
        matchGroups[mid].push(a);
      });

      const lastMatchId = Object.keys(matchGroups).reduce((a, b) =>
        parseInt(a) > parseInt(b) ? a : b
      );
      const lastMatchAppearances = matchGroups[lastMatchId];

      // Build a lookup by player id
      const statsByPlayer = {};
      lastMatchAppearances.forEach((a) => {
        if (a.player?.id) {
          statsByPlayer[String(a.player.id)] = {
            goals: a.goals,
            assists: a.assists,
          };
        }
      });

      setStats((prev) => {
        const next = { ...prev };
        players.forEach((p) => {
          const pid = String(p.id);
          if (statsByPlayer[pid]) {
            next[p.id] = { ...statsByPlayer[pid] };
          }
        });
        return next;
      });
    } catch (err) {
      setError(err.message || "Failed to load previous match.");
    } finally {
      setCopyLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button
          size="small"
          variant="text"
          onClick={handleCopyFromLastMatch}
          disabled={copyLoading || saving}
        >
          {copyLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Copy from last match
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Player</TableCell>
            <TableCell align="center">Goals</TableCell>
            <TableCell align="center">Assists</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {players.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{p.name}</TableCell>
              <TableCell align="center">
                <TextField
                  size="small"
                  type="number"
                  value={stats[p.id]?.goals ?? 0}
                  onChange={(e) =>
                    handleStatChange(p.id, "goals", e.target.value)
                  }
                  inputProps={{ min: 0, style: { width: 48, textAlign: "center" } }}
                  variant="standard"
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  size="small"
                  type="number"
                  value={stats[p.id]?.assists ?? 0}
                  onChange={(e) =>
                    handleStatChange(p.id, "assists", e.target.value)
                  }
                  inputProps={{ min: 0, style: { width: 48, textAlign: "center" } }}
                  variant="standard"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
        <Button variant="contained" onClick={handleSaveAll} disabled={saving}>
          {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Save All
        </Button>
        <Button variant="outlined" onClick={onDone} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default QuickEntryTable;
